import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface VehicleManagementProps {
  domain: string;
}

interface Vehicle {
  id: string;
  vehicleNumber: string;
  vehicleType: string;
  capacity: number;
  manufacturer: string;
  model: string;
  yearOfPurchase: string;
  insuranceExpiry: string;
  fitnessExpiry: string;
  status: 'Active' | 'Maintenance' | 'Inactive';
}

const VehicleManagement: React.FC<VehicleManagementProps> = ({ domain }) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    vehicleNumber: '',
    vehicleType: 'Bus',
    capacity: '',
    manufacturer: '',
    model: '',
    yearOfPurchase: '',
    insuranceExpiry: '',
    fitnessExpiry: '',
    status: 'Active' as 'Active' | 'Maintenance' | 'Inactive',
  });

  useEffect(() => {
    fetchVehicles();
  }, [domain]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const vehiclesRef = collection(db, `school-users/${domain}/transport-vehicles`);
      const snapshot = await getDocs(vehiclesRef);
      const vehicleList: Vehicle[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Vehicle));
      setVehicles(vehicleList);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      alert('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.vehicleNumber || !formData.capacity) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const vehicleData = {
        ...formData,
        capacity: Number(formData.capacity),
      };

      if (editingVehicle) {
        const vehicleRef = doc(db, `school-users/${domain}/transport-vehicles`, editingVehicle.id);
        await updateDoc(vehicleRef, vehicleData);
        alert('Vehicle updated successfully!');
      } else {
        await addDoc(collection(db, `school-users/${domain}/transport-vehicles`), vehicleData);
        alert('Vehicle added successfully!');
      }

      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('Failed to save vehicle');
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      vehicleNumber: vehicle.vehicleNumber,
      vehicleType: vehicle.vehicleType,
      capacity: vehicle.capacity.toString(),
      manufacturer: vehicle.manufacturer,
      model: vehicle.model,
      yearOfPurchase: vehicle.yearOfPurchase,
      insuranceExpiry: vehicle.insuranceExpiry,
      fitnessExpiry: vehicle.fitnessExpiry,
      status: vehicle.status,
    });
    setShowForm(true);
  };

  const handleDelete = async (vehicleId: string) => {
    if (!confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      await deleteDoc(doc(db, `school-users/${domain}/transport-vehicles`, vehicleId));
      alert('Vehicle deleted successfully!');
      fetchVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('Failed to delete vehicle');
    }
  };

  const resetForm = () => {
    setFormData({
      vehicleNumber: '',
      vehicleType: 'Bus',
      capacity: '',
      manufacturer: '',
      model: '',
      yearOfPurchase: '',
      insuranceExpiry: '',
      fitnessExpiry: '',
      status: 'Active',
    });
    setEditingVehicle(null);
    setShowForm(false);
  };

  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.vehicleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-success';
      case 'Maintenance': return 'bg-warning';
      case 'Inactive': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="bi bi-truck me-2"></i>Vehicle Management
        </h5>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          <i className="bi bi-plus-circle me-2"></i>
          {showForm ? 'Cancel' : 'Add Vehicle'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h6 className="card-title mb-3">
              {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Vehicle Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., DL-1234"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Vehicle Type *</label>
                  <select
                    className="form-select"
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Bus">Bus</option>
                    <option value="Van">Van</option>
                    <option value="Car">Car</option>
                    <option value="Mini Bus">Mini Bus</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Capacity *</label>
                  <input
                    type="number"
                    className="form-control"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    placeholder="e.g., 40"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Manufacturer</label>
                  <input
                    type="text"
                    className="form-control"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleInputChange}
                    placeholder="e.g., Tata Motors"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Model</label>
                  <input
                    type="text"
                    className="form-control"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="e.g., Starbus"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Year of Purchase</label>
                  <input
                    type="text"
                    className="form-control"
                    name="yearOfPurchase"
                    value={formData.yearOfPurchase}
                    onChange={handleInputChange}
                    placeholder="e.g., 2020"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Insurance Expiry</label>
                  <input
                    type="date"
                    className="form-control"
                    name="insuranceExpiry"
                    value={formData.insuranceExpiry}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Fitness Expiry</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fitnessExpiry"
                    value={formData.fitnessExpiry}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <button type="submit" className="btn btn-primary me-2">
                  <i className="bi bi-check-circle me-2"></i>
                  {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  <i className="bi bi-x-circle me-2"></i>Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by vehicle number, model, or manufacturer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-truck fs-1 d-block mb-3"></i>
              <p>No vehicles found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Vehicle Number</th>
                    <th>Type</th>
                    <th>Capacity</th>
                    <th>Manufacturer</th>
                    <th>Model</th>
                    <th>Year</th>
                    <th>Insurance Expiry</th>
                    <th>Fitness Expiry</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map((vehicle) => (
                    <tr key={vehicle.id}>
                      <td className="fw-semibold">{vehicle.vehicleNumber}</td>
                      <td>{vehicle.vehicleType}</td>
                      <td>{vehicle.capacity}</td>
                      <td>{vehicle.manufacturer}</td>
                      <td>{vehicle.model}</td>
                      <td>{vehicle.yearOfPurchase}</td>
                      <td>{vehicle.insuranceExpiry || 'N/A'}</td>
                      <td>{vehicle.fitnessExpiry || 'N/A'}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(vehicle.status)}`}>
                          {vehicle.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleEdit(vehicle)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(vehicle.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleManagement;
